import { pcoSetupPath, rockSetupPath } from '@/pages/onboarding/rock-setup/setupPath'
import { ROCK_CONNECTED_TOAST } from '@/pages/integrations/measurements/setup/copy'

/**
 * The one per-provider table the frontend keeps (shared contracts C1's
 * exception): custom card/settings sections and handoff rules for Rock and
 * Planning Center. A provider with no entry gets the generic card, the
 * catalog's labels and logo, and the catalog `setup_route` handoff.
 *
 * Every field is optional, so an entry may carry only copy (e.g.
 * `purgeCopy`). Only an entry that sets `handoff` replaces the catalog
 * `setup_route` handoff; one without it keeps the generic handoff.
 *
 * An entry that sets `handoff` MUST also define `setupPath` and
 * `connectedToast`: the OAuth return reads both unguarded for a handoff
 * entry (useIntegrationsData), so a missing `setupPath` throws and a missing
 * `connectedToast` shows an empty toast (pinned by overrides.test.js).
 *
 * @typedef {Object} Override
 * @property {'rock'|'planning_center'} [variant] - custom card/settings section
 * @property {string} [label]
 * @property {string} [description]
 * @property {string} [onboardingSource] - the Getting started answer this provider serves
 * @property {'always'|'pinned'} [handoff] - `HANDOFF_ALWAYS` or `HANDOFF_PINNED`
 * @property {(connectionId: string) => string} [setupPath]
 * @property {string} [connectedToast]
 * @property {boolean} [metricWizard]
 * @property {'pco'|null} [sourceEditor]
 * @property {boolean} [importAfterOAuth]
 * @property {string|null} [purgeCopy]
 */

/** Hand a successful return to the guided setup whatever `graphit_next` says. */
export const HANDOFF_ALWAYS = 'always'
/** Hand off only when `graphit_next` equals this override's `setupPath`. */
export const HANDOFF_PINNED = 'pinned'

export const PCO_CONNECTED_TOAST = 'Planning Center is connected. Let’s set it up.'

/** @type {Readonly<Record<string, Override>>} */
export const PROVIDER_OVERRIDES = Object.freeze({
  rockrms: Object.freeze({
    variant: 'rock',
    label: 'Rock RMS',
    description: 'Sync attendance, groups, and giving data from your Rock RMS instance.',
    onboardingSource: 'rock',
    handoff: HANDOFF_ALWAYS,
    setupPath: rockSetupPath,
    connectedToast: ROCK_CONNECTED_TOAST,
    metricWizard: true,
    sourceEditor: null,
    importAfterOAuth: false,
    purgeCopy: null,
  }),
  planning_center: Object.freeze({
    variant: 'planning_center',
    label: 'Planning Center',
    description: 'Bring in attendance, giving, group membership, list counts, and scheduled volunteers.',
    onboardingSource: 'planning_center',
    handoff: HANDOFF_PINNED,
    setupPath: pcoSetupPath,
    connectedToast: PCO_CONNECTED_TOAST,
    metricWizard: false,
    sourceEditor: 'pco',
    importAfterOAuth: true,
    purgeCopy: null,
  }),
})

/**
 * @param {string|null|undefined} key
 * @returns {Override|null}
 */
export function getProviderOverride(key) {
  if (!key || !Object.prototype.hasOwnProperty.call(PROVIDER_OVERRIDES, key)) return null
  return PROVIDER_OVERRIDES[key]
}

/**
 * Connections whose provider has an override satisfying `predicate`; a
 * provider with no override never qualifies.
 *
 * @param {Object[]} connections
 * @param {(override: Object) => boolean} predicate
 * @returns {Object[]}
 */
export function connectionsWithOverride(connections, predicate) {
  return (connections || []).filter((connection) => {
    const override = getProviderOverride(connection?.provider)
    return Boolean(override && predicate(override))
  })
}

/**
 * @param {string|null|undefined} source - an onboarding source key (`rock`, `planning_center`)
 * @returns {string|null} the provider key whose override answers that source
 */
export function overrideKeyForSource(source) {
  if (!source) return null
  const match = Object.entries(PROVIDER_OVERRIDES).find(([, override]) => override.onboardingSource === source)
  return match ? match[0] : null
}

/**
 * @param {string} label
 * @returns {string}
 */
export function connectedToastFor(label) {
  return `${label} is connected. Let’s set it up.`
}
